// frontend/src/components/EditCodeModal.jsx
import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import PropTypes from "prop-types";
import { updateCode } from "../api/codes";
import { moveCodeToFolder } from "../api/folders";
import { getCodeTags, addTagToCode, removeTagFromCode } from "../api/tags";
import { getUsage } from "../api/orgs";
import QrPayloadFields, {
  QR_TYPES,
  buildPayload,
  buildPayloadField,
  payloadToDefaults,
  diffPayload,
  INPUT_CLS,
  LABEL_CLS,
} from "./QrPayloadFields";
import CodeAdvancedFields, {
  buildPlatformDestinations,
  localToIso,
  isoToLocal,
} from "./CodeAdvancedFields";
import ABTestSection from "./ABTestSection";

const TYPE_LABEL = Object.fromEntries(QR_TYPES.map((t) => [t.value, t.label]));

/** Build the advanced-fields initial state from a stored code record. */
function advancedFromCode(codeObj) {
  const pd = codeObj.platform_destinations || null;
  return {
    folderId: codeObj.folder_id || "",
    tagIds: [],
    expiresAt: isoToLocal(codeObj.expires_at),
    activeFrom: isoToLocal(codeObj.active_from),
    platformEnabled: !!pd,
    iosUrl: pd?.ios || "",
    androidUrl: pd?.android || "",
    defaultUrl: pd?.default || "",
  };
}

/**
 * EditCodeModal — pre-filled modal for editing an existing QR code.
 *
 * Renders the field set matching the code's ``qr_type`` (URL keeps a single
 * destination/text field; typed codes such as vCard/WiFi expose their full
 * payload fields), pre-filled from the stored payload. The auto-generated
 * ``code`` token and the type are shown for reference but not editable.
 *
 * @param {object}   props
 * @param {object}   props.codeObj     The QR code record to edit (QRCodeRead shape)
 * @param {Function} props.onClose     Called to close without saving
 * @param {Function} props.onUpdated   Called with the updated code object on success
 */
function EditCodeModal({ codeObj, onClose, onUpdated, folders, tags }) {
  const [submitting, setSubmitting] = useState(false);
  // When set, the modal shows a confirmation step with the diff of changes.
  const [pending, setPending] = useState(null); // validated form values
  const [diffs, setDiffs] = useState([]);

  // Advanced fields (folder / tags / expiry / scheduling / platform).
  const [advanced, setAdvanced] = useState(() => advancedFromCode(codeObj));
  const [advancedTouched, setAdvancedTouched] = useState(false);
  const [initialTagIds, setInitialTagIds] = useState([]);
  // A/B testing is gated on the plan's ab_testing entitlement.
  const [abAvailable, setAbAvailable] = useState(false);

  const mergeAdvanced = (partial) => {
    setAdvanced((prev) => ({ ...prev, ...partial }));
    setAdvancedTouched(true);
  };

  const qrType = codeObj.qr_type || "url";
  const isUrl = qrType === "url";

  // Load the code's current tags + the plan's A/B entitlement. Gated on a real
  // code id so minimal test fixtures (no id) make no network calls.
  useEffect(() => {
    if (!codeObj.id) return;
    let cancelled = false;
    getCodeTags(codeObj.id)
      .then((codeTags) => {
        if (cancelled) return;
        const ids = codeTags.map((t) => t.id);
        setInitialTagIds(ids);
        setAdvanced((prev) => ({ ...prev, tagIds: ids }));
      })
      .catch(() => {});
    getUsage()
      .then((u) => {
        if (!cancelled) setAbAvailable(Boolean(u?.limits?.ab_testing));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [codeObj.id]);

  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors, isDirty },
  } = useForm({
    mode: "onTouched",
    defaultValues: {
      name: codeObj.name ?? "",
      is_active: codeObj.is_active,
      ...(isUrl
        ? { text: codeObj.text }
        : payloadToDefaults(qrType, codeObj.payload)),
    },
  });

  /** Compute the changes and move to the confirmation step. */
  const onReview = (values) => {
    const changes = [];
    const oldName = codeObj.name ?? "";
    const newName = values.name ?? "";
    if (oldName !== newName) {
      changes.push({
        label: "Label",
        before: oldName || "—",
        after: newName || "—",
      });
    }
    if (!!codeObj.is_active !== !!values.is_active) {
      changes.push({
        label: "Active",
        before: codeObj.is_active ? "Yes" : "No",
        after: values.is_active ? "Yes" : "No",
      });
    }
    if (isUrl) {
      if ((codeObj.text ?? "") !== (values.text ?? "")) {
        changes.push({
          label: "Destination",
          before: codeObj.text ?? "",
          after: values.text ?? "",
        });
      }
    } else {
      diffPayload(
        qrType,
        codeObj.payload,
        buildPayload(qrType, values),
      ).forEach((d) => changes.push(d));
    }
    setDiffs(changes);
    setPending(values);
  };

  /** Actually persist the pending changes. */
  const confirmSave = async () => {
    if (!pending) return;
    setSubmitting(true);
    try {
      const body = { name: pending.name || null, is_active: pending.is_active };
      if (isUrl) {
        body.text = pending.text;
      } else {
        Object.assign(body, buildPayloadField(qrType, pending));
      }
      // Expiry / scheduling / platform travel in the update body. The backend
      // treats null as "leave unchanged", so we only send values that are set.
      const expiresAt = localToIso(advanced.expiresAt);
      const activeFrom = localToIso(advanced.activeFrom);
      if (expiresAt) body.expires_at = expiresAt;
      if (activeFrom) body.active_from = activeFrom;
      const platform = buildPlatformDestinations(advanced);
      if (platform) body.platform_destinations = platform;

      const updated = await updateCode(codeObj.code, body);

      // Folder + tag membership change through their own endpoints. Best-effort
      // so a secondary failure doesn't mask the successful core update.
      if (codeObj.id) {
        try {
          if ((codeObj.folder_id || "") !== advanced.folderId) {
            await moveCodeToFolder(codeObj.id, advanced.folderId || null);
          }
          const added = advanced.tagIds.filter(
            (t) => !initialTagIds.includes(t),
          );
          const removed = initialTagIds.filter(
            (t) => !advanced.tagIds.includes(t),
          );
          for (const tagId of added) await addTagToCode(codeObj.id, tagId);
          for (const tagId of removed)
            await removeTagFromCode(codeObj.id, tagId);
        } catch {
          toast.error("Code updated, but folder/tags didn't fully apply.");
        }
      }

      toast.success("Code updated.");
      onUpdated(updated);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Failed to update code. Please try again.");
      setPending(null);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-5">
          <h2 className="text-base font-semibold text-gray-900">Edit Code</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            Token: <span className="font-mono">{codeObj.code}</span> ·{" "}
            <span>{TYPE_LABEL[qrType] || qrType}</span>
          </p>
        </div>

        {pending ? (
          /* ── Confirmation / diff step ─────────────────────────────────── */
          <div className="space-y-4">
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="text-sm font-medium text-amber-800">
                This QR code is already live.
              </p>
              <p className="mt-1 text-xs text-amber-700">
                Updating it changes what every existing copy resolves to —
                anything already printed or shared will now point to the new
                content. The QR image itself does not change.
              </p>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">
                {diffs.length > 0
                  ? "Review your changes:"
                  : "No content changes — only metadata will be saved."}
              </p>
              <ul className="space-y-2">
                {diffs.map((d) => (
                  <li
                    key={d.label}
                    className="text-xs border border-gray-200 rounded-lg p-2"
                  >
                    <span className="font-medium text-gray-700">{d.label}</span>
                    <div className="mt-1 flex items-center gap-2">
                      <span className="line-through text-red-500 break-all">
                        {d.before || "—"}
                      </span>
                      <span className="text-gray-400">→</span>
                      <span className="text-green-600 break-all">
                        {d.after || "—"}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setPending(null)}
                disabled={submitting}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Back
              </button>
              <button
                type="button"
                onClick={confirmSave}
                disabled={submitting}
                className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {submitting ? "Updating…" : "Update QR"}
              </button>
            </div>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit(onReview)}
            noValidate
            className="space-y-4"
          >
            {/* Human-readable label */}
            <div>
              <label className={LABEL_CLS} htmlFor="edit-name">
                Label{" "}
                <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                id="edit-name"
                type="text"
                placeholder="e.g. Q4 Promo"
                className={INPUT_CLS}
                {...register("name", {
                  maxLength: { value: 200, message: "Max 200 characters." },
                })}
              />
              {errors.name && (
                <p className="mt-1 text-xs text-red-600">
                  {errors.name.message}
                </p>
              )}
            </div>

            {isUrl ? (
              /* Legacy/URL codes keep the lenient single text field. */
              <div>
                <label className={LABEL_CLS} htmlFor="edit-text">
                  Destination URL or text
                </label>
                <input
                  id="edit-text"
                  type="text"
                  className={INPUT_CLS}
                  {...register("text", {
                    required: "Destination is required.",
                  })}
                />
                {errors.text && (
                  <p className="mt-1 text-xs text-red-600">
                    {errors.text.message}
                  </p>
                )}
              </div>
            ) : (
              <QrPayloadFields
                qrType={qrType}
                register={register}
                errors={errors}
                idPrefix="edit"
                control={control}
                watch={watch}
              />
            )}

            {/* Active toggle */}
            <div className="flex items-center justify-between py-2 border-t border-gray-100">
              <div>
                <p className="text-sm font-medium text-gray-700">Active</p>
                <p className="text-xs text-gray-400">
                  Inactive codes return 404 to scanners
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  {...register("is_active")}
                />
                <div
                  className="w-10 h-6 bg-gray-200 rounded-full peer peer-checked:bg-blue-600
                after:content-[''] after:absolute after:top-[2px] after:left-[2px]
                after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all
                peer-checked:after:translate-x-4"
                />
              </label>
            </div>

            {/* Folder / tags / expiry / scheduling / platform */}
            <CodeAdvancedFields
              folders={folders}
              tags={tags}
              value={advanced}
              onChange={mergeAdvanced}
            />

            {/* A/B testing (Pro+) — only for persisted codes (need an id). */}
            {codeObj.id && (
              <ABTestSection codeId={codeObj.id} available={abAvailable} />
            )}

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || (!isDirty && !advancedTouched)}
                className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                Review changes
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

EditCodeModal.propTypes = {
  codeObj: PropTypes.shape({
    id: PropTypes.number,
    code: PropTypes.string.isRequired,
    name: PropTypes.string,
    text: PropTypes.string.isRequired,
    is_active: PropTypes.bool.isRequired,
    qr_type: PropTypes.string,
    payload: PropTypes.object,
    folder_id: PropTypes.string,
    expires_at: PropTypes.string,
    active_from: PropTypes.string,
    platform_destinations: PropTypes.object,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onUpdated: PropTypes.func.isRequired,
  folders: PropTypes.array,
  tags: PropTypes.array,
};
EditCodeModal.defaultProps = { folders: [], tags: [] };

export default EditCodeModal;
